import React from 'react';
import { Card } from '../ui/card';
import { Idea } from '../../types/idea-machine';
import { Brain, Trash2, Calendar, Clock, BookOpen } from 'lucide-react';
import { format } from 'date-fns';

interface IdeaCardProps {
  idea: Idea;
  onClick: () => void;
  onDelete?: () => void;
  showDelete?: boolean;
  onTogglePrivacy?: (isPublic: boolean) => void;
}

export const IdeaCard: React.FC<IdeaCardProps> = ({ idea, onClick, onDelete, showDelete, onTogglePrivacy }) => {
  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete();
    }
  };

  const handlePrivacyToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onTogglePrivacy) {
      onTogglePrivacy(!idea.isPublic);
    }
  };

  return (
    <Card
      className="p-4 bg-white dark:bg-gray-800 border dark:border-gray-700 shadow-sm hover:shadow-lg transition-all cursor-pointer flex flex-col justify-between h-full"
      onClick={onClick}
    >
      <div>
        <div className="flex items-start justify-between mb-2">
          <div className="p-2 bg-blue-50 dark:bg-blue-900/50 rounded-lg">
            <Brain className="h-5 w-5 text-blue-500" />
          </div>
          {showDelete && onDelete && (
            <button
              onClick={handleDelete}
              className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/50 rounded-md"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
        <h3 className="font-semibold text-gray-900 dark:text-white mb-2 truncate" title={idea.seedPrompt}>
          {idea.seedPrompt || 'No prompt'}
        </h3>
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-gray-500 dark:text-gray-400 mt-auto">
        <span className="inline-flex items-center gap-1.5">
          <div className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded-md text-gray-600 dark:text-gray-300">
            {idea.seedCategory || 'Uncategorized'}
          </div>
        </span>
        <span className="inline-flex items-center gap-1.5">
          <BookOpen className="h-3 w-3" />
          {idea.content.length} ideas
        </span>
        {onTogglePrivacy && (
          <div className="flex items-center gap-2 ml-auto" onClick={(e) => e.stopPropagation()}>
            <span className={`text-[10px] uppercase font-bold tracking-wider ${idea.isPublic ? 'text-green-500' : 'text-gray-400'}`}>
              {idea.isPublic ? 'Public' : 'Private'}
            </span>
            <div 
              onClick={handlePrivacyToggle}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                idea.isPublic ? 'bg-green-500' : 'bg-gray-200 dark:bg-gray-700'
              }`}
            >
              <span
                className={`${
                  idea.isPublic ? 'translate-x-4' : 'translate-x-1'
                } inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform`}
              />
            </div>
          </div>
        )}
        <span className="inline-flex items-center gap-1.5">
          <Clock className="h-3 w-3" />
          {idea.timeSpent}s
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Calendar className="h-3 w-3" />
          {format(new Date(idea.timestamp), 'MMM d, yyyy')}
        </span>
      </div>
    </Card>
  );
};
